import {
  AnalyticsTotalType,
  BulkUpdateDirectories,
  MutationBulkUpdateDirectoriesArgs
} from '@dtx-company/flow-codegen/src/code/generated.types'
import {
  BULK_UPDATE_DIRECTORIES_MUTATION,
  UPDATE_FOLDER_MUTATION,
  UPDATE_FOLDER_MUTATION_WITH_DESCRIPTION
} from '../gql/mutations/flowcode'
import {
  FetchFolderQueryResponseType,
  FetchFolderVariablesType,
  UpdateFolderVariablesType
} from '../types/folder.types'
import { FlowcodeNodeType } from '@dtx-company/inter-app/src/types/flowcode'
import { GET_FOLDER_LEVEL_QUERY } from '../gql/queries/flowcode'
import { PageInfoType } from '../types/api.types'
import { getGqlError } from './errors'
import { gqlFetcher } from '@dtx-company/inter-app/src/services/gqlFetcher'
import { reduceFlowcodes } from './my-flowcodes'
import {
  sendErrorNotification,
  sendSuccessNotification
} from '@dtx-company/inter-app/src/utils/notifications'

export const updateFolder = async (
  variables: UpdateFolderVariablesType
): Promise<{ updateDirectory: { ok: boolean } }> => {
  return gqlFetcher(UPDATE_FOLDER_MUTATION, variables)
}

export const updateFolderWithDescription = async (
  variables: UpdateFolderVariablesType
): Promise<{ updateDirectory: { ok: boolean } }> => {
  return gqlFetcher(UPDATE_FOLDER_MUTATION_WITH_DESCRIPTION, variables)
}

export const handleEditFolder = async (
  name: string,
  id: string,
  onSuccess?: () => void
): Promise<void> => {
  try {
    const updateFolderResponse = await updateFolder({
      name,
      directoryId: id
    })
    if (!updateFolderResponse.updateDirectory.ok) throw new Error('error updating folder name')
    sendSuccessNotification('Updated folder name')
    onSuccess && onSuccess()
  } catch (e) {
    console.error('error updating folder name', e.message)
    const { message } = getGqlError(e, false)
    sendErrorNotification(message)
  }
}

// SWR does not merge the responses. This util is used to manually merge the responses. :(
export const parseFetchFolderResponse = (
  data?: FetchFolderQueryResponseType[]
): {
  flowcodes: FlowcodeNodeType[] | undefined
  pageInfo: PageInfoType | undefined
  highlightedFlowcodes: FlowcodeNodeType[] | undefined
  analytics: AnalyticsTotalType | undefined
  directory: FetchFolderQueryResponseType['directory'] | undefined
} => {
  const lastElement = data && data[data.length - 1]
  // the pagination for this query is ignored, use the most recent info
  const highlightedFlowcodes = lastElement?.directory.highlighted?.edges
  const pageInfo = lastElement?.myFlowcodes?.allBatches.pageInfo
  const flowcodes = reduceFlowcodes(data, true)
  const analytics = lastElement?.directory?.analytics?.total

  return {
    flowcodes,
    pageInfo,
    highlightedFlowcodes,
    analytics,
    directory: lastElement?.directory
  }
}

export const getFetchFolderKey = (
  pageIndex: number,
  previousPageData: FetchFolderQueryResponseType | null,
  variables: FetchFolderVariablesType,
  token?: string
): string[] | null => {
  const key = pageIndex.toString()
  const pageInfo = previousPageData?.myFlowcodes?.allBatches?.pageInfo
  if ((previousPageData && !pageInfo?.hasNextPage) || !token) {
    return null
  }

  if (pageIndex === 0 || !pageInfo) {
    return [
      key,
      '',
      token,
      variables.orderBy || '',
      variables.contains || '',
      variables.directoryId,
      variables.withStudioConfig?.toString() || '',
      variables.withAncestors?.toString() || ''
    ]
  }

  return [
    key,
    pageInfo.endCursor,
    token,
    variables.orderBy || '',
    variables.contains || '',
    variables.directoryId,
    variables.withStudioConfig?.toString() || '',
    variables.withAncestors?.toString() || ''
  ]
}

interface FolderLevelResponse {
  directory: {
    level: number
  }
}

export const getFolderLevel = (directoryId: string): Promise<FolderLevelResponse> => {
  return gqlFetcher<
    FolderLevelResponse,
    {
      directoryId: string
    }
  >(GET_FOLDER_LEVEL_QUERY, {
    directoryId
  })
}

export interface BulkUpdateDirectoriesResponse {
  bulkUpdateDirectories: BulkUpdateDirectories
}

export const bulkUpdateDirectories = async (
  variables: MutationBulkUpdateDirectoriesArgs
): Promise<BulkUpdateDirectoriesResponse> => {
  return gqlFetcher(BULK_UPDATE_DIRECTORIES_MUTATION, variables)
}
